/**
 * Tout ce que la scène 3D décide, **sans three.js et sans DOM**.
 *
 * Les matières, les vues préréglées, l'orientation du gizmo et le lissage du
 * niveau audio sont de l'arithmétique : les sortir d'ici permet de les vérifier
 * sans WebGL, alors qu'un test de rendu ne dirait jamais qu'une matière a viré
 * au vert ou que le gizmo a un signe à l'envers.
 */

/**
 * Les cinq matières, dans l'ordre du cycle — mat → satiné → métallisé → miroir
 * → luminescent.
 */
export const MATERIALS = ['matte', 'satin', 'metal', 'mirror', 'glow'] as const;
export type MaterialName = (typeof MATERIALS)[number];

/** Réglages d'une matière. Deux jeux : le corps, et les extrémités foncées. */
export interface MaterialSpec {
  readonly body: { readonly color: string; readonly roughness: number };
  readonly dark: { readonly color: string; readonly roughness: number };
  readonly metalness: number;
  readonly envMapIntensity: number;
  /**
   * Intensité de l'émission. Zéro pour toutes les matières sauf `glow`.
   *
   * La couleur émise n'est jamais choisie : c'est celle de la partie. Steve
   * éclaire donc dans son propre orange, et la règle absolue du site tient par
   * construction plutôt que par vigilance.
   */
  readonly emissiveIntensity: number;
}

/**
 * Les quatre variantes.
 *
 * **Elles ne jouent que sur la matière, jamais sur la teinte** : rugosité,
 * métallicité, intensité des reflets. C'est la seule constante du personnage sur
 * tous les skins — Steve est orange, et le reste dans le miroir comme dans le
 * mat. Le miroir monte à peine en clarté pour que les reflets ne le noircissent
 * pas, il ne change pas de couleur.
 */
export const MATERIAL_SPECS: Record<MaterialName, MaterialSpec> = {
  matte: {
    body: { color: '#f97316', roughness: 1 },
    dark: { color: '#c2410c', roughness: 1 },
    metalness: 0,
    envMapIntensity: 0.6,
    emissiveIntensity: 0,
  },
  satin: {
    body: { color: '#f97316', roughness: 0.42 },
    dark: { color: '#c2410c', roughness: 0.5 },
    metalness: 0.12,
    envMapIntensity: 1,
    emissiveIntensity: 0,
  },
  metal: {
    body: { color: '#f97316', roughness: 0.3 },
    dark: { color: '#c2410c', roughness: 0.34 },
    metalness: 0.92,
    envMapIntensity: 1.25,
    emissiveIntensity: 0,
  },
  mirror: {
    body: { color: '#ffa155', roughness: 0.02 },
    dark: { color: '#d4530f', roughness: 0.04 },
    metalness: 1,
    envMapIntensity: 2.1,
    emissiveIntensity: 0,
  },
  /*
   * Luminescent : le seul à s'éclairer lui-même. Mat et sans métal, sinon
   * l'émission se noie dans les reflets du studio ; l'environnement est baissé
   * pour la même raison. L'œil et la pupille n'émettent pas — ils ne passent
   * pas par cette table.
   */
  glow: {
    body: { color: '#f97316', roughness: 0.62 },
    dark: { color: '#c2410c', roughness: 0.66 },
    metalness: 0,
    envMapIntensity: 0.45,
    emissiveIntensity: 0.9,
  },
};

/** Matière suivante dans le cycle. L'ordre ne change jamais. */
export function nextMaterial(current: MaterialName): MaterialName {
  const index = MATERIALS.indexOf(current);
  return MATERIALS[(index + 1) % MATERIALS.length] ?? 'matte';
}

/** Les quatre vues préréglées : l'alternative clavier à l'orbite à la souris. */
export const VIEWS = ['threeQuarter', 'front', 'side', 'top'] as const;
export type ViewName = (typeof VIEWS)[number];

export interface CameraSpot {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Où se place la caméra pour chaque vue.
 *
 * **Steve regarde vers `+x`** : son œil est à `+x`, sa queue à `-x`. « Face »
 * est donc la caméra posée sur l'axe X — celle qui le regarde dans les yeux — et
 * « profil » celle posée sur Z, qui le prend de flanc. L'inverse donnait deux
 * boutons qui affichaient chacun ce que l'autre annonçait. La vue de face est
 * aussi plus rapprochée : de face, un poisson est deux fois moins large que long.
 */
export const VIEW_SPOTS: Record<ViewName, CameraSpot> = {
  threeQuarter: { x: 2.9, y: 2.25, z: 3.5 },
  front: { x: 3.4, y: 1.5, z: 0.01 },
  side: { x: 0.01, y: 1.5, z: 4.6 },
  top: { x: 0.01, y: 5.2, z: 0.8 },
};

/** Ce que vise la caméra, dans toutes les vues. */
export const CAMERA_TARGET: CameraSpot = { x: 0, y: 1.2, z: 0 };

export interface Orientation {
  /** Azimut en degrés, 0 = face, positif vers la droite. */
  readonly azimuth: number;
  /** Élévation en degrés, positive au-dessus de l'horizon. */
  readonly elevation: number;
}

/**
 * Orientation de la caméra, telle que le gizmo doit la refléter.
 *
 * C'est le détail qui fait que l'habillage « connaît » la scène : les trois
 * barres d'axes tournent exactement avec la caméra réelle. Un signe à l'envers
 * ici et le gizmo part dans le sens contraire — invisible sur une capture, criant
 * dès qu'on fait tourner le modèle.
 */
export function orientationOf(camera: CameraSpot, target: CameraSpot = CAMERA_TARGET): Orientation {
  const dx = camera.x - target.x;
  const dy = camera.y - target.y;
  const dz = camera.z - target.z;
  const flat = Math.sqrt(dx * dx + dz * dz);
  return {
    azimuth: (Math.atan2(dx, dz) * 180) / Math.PI,
    elevation: (Math.atan2(dy, flat) * 180) / Math.PI,
  };
}

/** « 043 » — l'azimut se lit toujours sur trois chiffres, comme un cap. */
export function formatAzimuth(degrees: number): string {
  const wrapped = ((Math.round(degrees) % 360) + 360) % 360;
  return String(wrapped).padStart(3, '0');
}

/** Lissage du niveau audio : la valeur suit la musique sans sauter. */
export const LEVEL_ATTACK = 0.25;
export const LEVEL_RELEASE = 0.08;

/**
 * Rapproche `level` de `target`.
 *
 * Deux vitesses : rapide quand la musique monte, lente quand elle s'arrête. Sans
 * la seconde, couper la chanson fige Steve d'un coup en plein saut.
 */
export function smoothLevel(level: number, target: number, playing: boolean): number {
  const rate = playing ? LEVEL_ATTACK : LEVEL_RELEASE;
  const next = level + (target - level) * rate;
  return Math.abs(next) < 1e-4 ? 0 : next;
}

/**
 * La pose du modèle à un instant donné, dictée par le temps et le niveau.
 *
 * **Elle ne décrit que le corps.** La rotation automatique est le plateau
 * tournant du studio, pas un mouvement de Steve : elle a son propre bouton, et
 * elle vit dans `spinRate`.
 */
export interface Pose {
  readonly torsoY: number;
  readonly torsoScaleX: number;
  readonly torsoScaleY: number;
  readonly torsoTilt: number;
  /** Rotation des deux bras, déphasés d'un demi-tour. */
  readonly arms: readonly [number, number];
  readonly legs: readonly [number, number];
}

/** Vitesse du plateau tournant au repos, en radians par seconde. */
export const SPIN_BASE = 0.42;

/** Ce que la musique ajoute à la vitesse du plateau, au niveau maximal. */
export const SPIN_GAIN = 1.6;

/**
 * Vitesse de la rotation automatique.
 *
 * Séparée de la pose parce que les deux boutons sont séparés : couper la danse
 * ne doit pas immobiliser le plateau, et couper la rotation ne doit pas
 * empêcher Steve de respirer.
 */
export function spinRate(level: number): number {
  return SPIN_BASE + level * SPIN_GAIN;
}

/**
 * Hauteur du torse au repos.
 *
 * Déduite du dessin de référence (`Steve.module.css`) et non choisie : le centre
 * du corps y est à 119 px au-dessus de la plante des pieds, soit 1,35 unité. Les
 * cinq centimètres de plus sont l'amplitude de la respiration — au point bas,
 * Steve touche le sol au lieu de flotter au-dessus.
 */
export const TORSO_REST_Y = 1.4;

/**
 * La danse.
 *
 * Une respiration permanente en `sin(t)`, et par-dessus le niveau audio qui fait
 * sauter et écraser. `still` coupe **tout** mouvement propre et laisse la pose de
 * repos.
 *
 * C'est le seul sens utile pour le bouton « danse » : tant qu'il ne coupait que
 * la part audio, l'éteindre ne se voyait pas — le silence met déjà le niveau à
 * zéro, et Steve continuait de respirer exactement pareil. `prefers-reduced-motion`
 * passe par le même chemin, sans pour autant empêcher le visiteur de faire
 * tourner le modèle lui-même.
 */
export function poseAt(time: number, level: number, still: boolean): Pose {
  if (still) {
    return {
      torsoY: TORSO_REST_Y,
      torsoScaleX: 1,
      torsoScaleY: 1,
      torsoTilt: 0,
      arms: [0, 0],
      legs: [0, 0],
    };
  }

  const armSwing = 0.22 + level * 1.5;
  const legSwing = 0.14 + level * 0.9;
  return {
    torsoY: TORSO_REST_Y + Math.sin(time * 1.9) * 0.05 + level * 0.34,
    torsoScaleX: 1 + level * 0.07,
    torsoScaleY: 1 - level * 0.05,
    torsoTilt: Math.sin(time * 1.3) * 0.035 + level * 0.1,
    arms: [Math.sin(time * 2.4) * armSwing, Math.sin(time * 2.4 + Math.PI) * armSwing],
    legs: [Math.sin(time * 2.4) * legSwing, Math.sin(time * 2.4 + Math.PI) * legSwing],
  };
}
